// src/pages/ProjectsPage.jsx
import React, { useEffect, useState } from "react";
import { Input, Button, Select, Empty, Avatar } from "antd";
import {
  AppstoreOutlined,
  CalendarOutlined,
  CheckSquareOutlined,
  FileTextOutlined,
  FolderOpenOutlined,
  PlusOutlined,
  SearchOutlined,
  TableOutlined,
  UserOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import DOMPurify from "dompurify";
import ProjectCard from "./components/ProjectCard";
import ProjectFormDrawer from "./components/ProjectFormDrawer";
import useProjects from "hooks/projects/useProjects";
import { Spinner } from "react-bootstrap";
import { ConfirmDeleteModal } from "components/common/ConfirmDeleteModal";
import useIterations from "hooks/projects/useIterations";
import { useTags } from "hooks";
import ProjectsTableView from "./components/ProjectsTableView";
import RecentItems from "./components/RecentItems";
import ProjectDetailsDrawer from "./components/ProjectDetailsDrawer";
import { useAuth } from "contexts/AuthContext";
import { OLIVEEB_COLORS } from "workspaces/colors";

const Projects = () => {
  const { projects, loading, createProject, updateProject, deleteProject } =
    useProjects();
  const { loading: iterationsLoading } = useIterations();
  const { tags } = useTags();
  const { user } = useAuth();

  const [normalizedProjects, setNormalizedProjects] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter] = useState(null);
  const [activeTab, setActiveTab] = useState("kanban");
  const [selectedTags, setSelectedTags] = useState([]);
  const [isFiltering, setIsFiltering] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [isDetailsDrawerOpen, setIsDetailsDrawerOpen] = useState(false);
  const [pendingDetailsProject, setPendingDetailsProject] = useState(null);
  const [detailsProjectId, setDetailsProjectId] = useState(null);

  const normalizeProjects = (projectsFromAPI) => {
    return projectsFromAPI.map((p) => ({
      id: p.id,
      name: p.name,
      code: p.code,
      description: p.description,
      ownerName: p.ownerName || "Unknown",
      // tags: p.tags ? p.tags.map((t) => t.name) : [],
      tags: p.tags,
      iterationCount: p.iterationCount,
      taskCount: p.taskCount || 0,
      createdAt: p.createdAt,
      updatedAt: p.updatedAt,
      targetPoints: p.targetPoints,
    }));
  };

  // debounce search input
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(search);
    }, 300);

    return () => clearTimeout(handler);
  }, [search]);

  useEffect(() => {
    if (projects && projects.length > 0) {
      setNormalizedProjects(normalizeProjects(projects));
    }
  }, [projects]);

  const filteredProjects = normalizedProjects.filter((project) => {
    const matchesSearch = project.name
      .toLowerCase()
      .includes(debouncedSearch.toLowerCase());

    const matchesStatus = !statusFilter || project.status === statusFilter;

    const matchesTags =
      selectedTags.length === 0 ||
      (project.tags || []).some((tag) => selectedTags.includes(tag.name));

    return matchesSearch && matchesStatus && matchesTags;
  });

  const detailsProject =
    filteredProjects.find((project) => project.id === detailsProjectId) ||
    filteredProjects[0] ||
    null;

  useEffect(() => {
    setIsFiltering(true);
    const timeout = setTimeout(() => setIsFiltering(false), 250);
    return () => clearTimeout(timeout);
  }, [search, statusFilter, selectedTags]);

  useEffect(() => {
    if (!isDrawerOpen && pendingDetailsProject) {
      setEditingProject(pendingDetailsProject);
      setIsDetailsDrawerOpen(true);
      setPendingDetailsProject(null);
    }
  }, [isDrawerOpen, pendingDetailsProject]);

  useEffect(() => {
    if (activeTab !== "details") return;

    const stillVisible = filteredProjects.some(
      (project) => project.id === detailsProjectId,
    );

    if (!stillVisible) {
      setDetailsProjectId(filteredProjects[0]?.id || null);
    }
  }, [activeTab, detailsProjectId, filteredProjects]);

  const openDetailsDrawer = (project) => {
    setEditingProject(project);
    setIsDetailsDrawerOpen(true);
  };

  const openEditDrawer = (project) => {
    setEditingProject(project);
    setIsDrawerOpen(true);
  };

  const handleProjectTagsUpdate = (projectId, updatedTags) => {
    setNormalizedProjects((prev) =>
      prev.map((project) =>
        project.id === projectId ? { ...project, tags: updatedTags } : project,
      ),
    );
  };

  const handleSubmitProject = async (values) => {
    const previousProjects = normalizedProjects;
    let createdProject = null;

    try {
      setDrawerLoading(true);
      const tagIds = values.tags || [];

      if (editingProject) {
        const updatedProject = {
          ...editingProject,
          name: values.name,
          description: values.description,
          ownerName: values.ownerName,
        };

        setNormalizedProjects((prev) =>
          prev.map((p) => (p.id === editingProject.id ? updatedProject : p)),
        );

        if (updatedProject) {
          await updateProject(editingProject.id, updatedProject);
        }
      } else {
        const tempId = Date.now();

        const fullTags = tags.filter((t) => tagIds.includes(t.id));

        const optimisticProject = {
          id: tempId,
          name: values.name,
          code: values.code || "",
          description: values.description,
          ownerName: values.ownerName || "Unknown",
          tags: fullTags,
          iterationCount: 0,
          taskCount: 0,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          targetPoints: values.targetPoints || 0,
        };

        setNormalizedProjects((prev) => [optimisticProject, ...prev]);

        const created = await createProject({
          ...values,
          tagIds,
          creatorUserId: user?.id || null,
        });

        const normalizedCreated = normalizeProjects([created])[0];
        setNormalizedProjects((prev) =>
          prev.map((p) => (p.id === tempId ? normalizedCreated : p)),
        );
        createdProject = normalizedCreated;
      }
    } catch (err) {
      setNormalizedProjects(previousProjects); // fallback when error occurs
      console.error("Failed to create/update project", err);
    } finally {
      setDrawerLoading(false);
      setIsDrawerOpen(false);
      if (createdProject) {
        setPendingDetailsProject(createdProject);
      } else {
        setEditingProject(null);
      }
    }
  };

  const handleDeleteProject = async () => {
    if (!projectToDelete) return;

    const previousProjects = normalizedProjects;
    setDeleteLoading(true);

    try {
      setNormalizedProjects((prev) =>
        prev.filter((p) => p.id !== projectToDelete.id),
      );

      await deleteProject(projectToDelete.id);
    } catch (err) {
      setNormalizedProjects(previousProjects); // rollback
      console.error("Failed to delete project", err);
    } finally {
      setProjectToDelete(null);
      setDeleteLoading(false);
    }
  };

  if (loading || iterationsLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: 400 }}
      >
        <Spinner animation="border" variant="success" />
        <span className="ms-3" style={{ fontSize: "13.33px" }}>
          Loading...
        </span>
      </div>
    );
  }

  return (
    <div className="project-page">
      {/* Header */}
      <div className="project-header">
        <div className="project-title">
          <div className="project-icon">C</div>
          <div>
            <h1 style={{ margin: 0, padding: 0 }}>Projects</h1>
            <p style={{ margin: 0, padding: 0, fontSize: 12 }}>
              Manage and organize your team’s projects
            </p>
          </div>
        </div>

        <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
          <RecentItems />
          <Avatar.Group max={3}>
            <Avatar>AL</Avatar>
            <Avatar>DT</Avatar>
            <Avatar>MK</Avatar>
          </Avatar.Group>
        </div>
      </div>

      <div className="project-toolbar">
        <div className="custom-tabs">
          <div
            className={`tab-item ${activeTab === "kanban" ? "active" : ""}`}
            onClick={() => setActiveTab("kanban")}
          >
            <AppstoreOutlined />
            Kanban
          </div>

          <div
            className={`tab-item ${activeTab === "table" ? "active" : ""}`}
            onClick={() => setActiveTab("table")}
          >
            <TableOutlined />
            Table
          </div>

          <div
            className={`tab-item ${activeTab === "details" ? "active" : ""}`}
            onClick={() => setActiveTab("details")}
          >
            <FileTextOutlined />
            Details
          </div>
        </div>

        {/* Right - Controls */}
        <div className="toolbar-actions">
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search..."
            className="search-input"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ width: 220 }}
          />

          <Select
            mode="multiple"
            placeholder="Filter by tag"
            style={{ width: 220 }}
            value={selectedTags}
            onChange={setSelectedTags}
            options={tags.map((t) => ({
              value: t.name,
              label: t.name,
            }))}
            maxTagCount={1}
          />

          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="new-task-btn"
            onClick={() => {
              setEditingProject(null);
              setIsDrawerOpen(true);
            }}
          >
            New Project
          </Button>
        </div>
      </div>

      {selectedTags.length > 0 && (
        <div className="active-filter">
          Filtering by tag:
          {selectedTags.map((tag) => (
            <span key={tag.id} className="active-filter-tag">
              {tag}
              <span
                className="remove-filter"
                onClick={() =>
                  setSelectedTags((prev) => prev.filter((t) => t !== tag))
                }
              >
                ✕
              </span>
            </span>
          ))}
          <span className="clear-all" onClick={() => setSelectedTags([])}>
            Clear all
          </span>
        </div>
      )}

      {/* Grid */}
      {activeTab === "kanban" && filteredProjects.length > 0 && (
        <div className={`projects-grid ${isFiltering ? "filtering" : ""}`}>
          {filteredProjects.map((project, index) => (
            <ProjectCard
              index={index}
              key={project.id}
              project={project}
              selectedTags={selectedTags}
              onTagClick={(tag) =>
                setSelectedTags((prev) =>
                  prev.includes(tag)
                    ? prev.filter((t) => t !== tag)
                    : [...prev, tag],
                )
              }
              onView={() => openDetailsDrawer(project)}
              onEdit={() => openEditDrawer(project)}
              onDelete={() => {
                setProjectToDelete(project);
                setDeleteModalOpen(true);
              }}
            />
          ))}
        </div>
      )}

      {/* Only show Empty in Kanban view */}
      {filteredProjects.length === 0 && (
        <div className="projects-empty">
          <Empty description="No projects found" />
        </div>
      )}

      {activeTab === "table" && filteredProjects.length > 0 && (
        <ProjectsTableView
          projects={filteredProjects}
          openDetailsDrawer={openDetailsDrawer}
          openEditDrawer={openEditDrawer}
          onDelete={(project) => {
            setProjectToDelete(project);
            setDeleteModalOpen(true);
          }}
          selectedTags={selectedTags}
          setSelectedTags={setSelectedTags}
        />
      )}

      {activeTab === "details" && filteredProjects.length > 0 && (
        <div
          style={{
            display: "grid",
            gridTemplateColumns:
              "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
            gap: 18,
            alignItems: "start",
          }}
        >
          <div
            style={{
              border: `1px solid ${OLIVEEB_COLORS.oliveebGreenSoft}`,
              borderRadius: 8,
              background: OLIVEEB_COLORS.white,
              boxShadow: "0 12px 30px rgba(9, 3, 89, 0.08)",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                padding: "14px 16px",
                borderBottom: `1px solid ${OLIVEEB_COLORS.oliveebGreenSoft}`,
                background: `linear-gradient(135deg, ${OLIVEEB_COLORS.white} 0%, ${OLIVEEB_COLORS.greenSoft2} 100%)`,
              }}
            >
              <div
                style={{
                  color: OLIVEEB_COLORS.oliveebGreen,
                  fontSize: 12,
                  fontWeight: 800,
                  textTransform: "uppercase",
                }}
              >
                Project Details
              </div>
              <div
                style={{
                  color: OLIVEEB_COLORS.textMuted,
                  fontSize: 12,
                  marginTop: 2,
                }}
              >
                {filteredProjects.length} project
                {filteredProjects.length === 1 ? "" : "s"} in view
              </div>
            </div>

            <div style={{ maxHeight: 520, overflowY: "auto", padding: 8 }}>
              {filteredProjects.map((project) => {
                const selected = detailsProject?.id === project.id;

                return (
                  <button
                    key={project.id}
                    type="button"
                    onClick={() => setDetailsProjectId(project.id)}
                    style={{
                      width: "100%",
                      border: `1px solid ${
                        selected
                          ? OLIVEEB_COLORS.oliveebGreen
                          : OLIVEEB_COLORS.border
                      }`,
                      borderRadius: 8,
                      background: selected
                        ? OLIVEEB_COLORS.oliveebGreenSoft
                        : OLIVEEB_COLORS.white,
                      color: OLIVEEB_COLORS.textPrimary,
                      padding: 12,
                      textAlign: "left",
                      cursor: "pointer",
                      marginBottom: 8,
                      boxShadow: selected
                        ? "0 10px 24px rgba(39, 174, 48, 0.14)"
                        : "none",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        gap: 10,
                        alignItems: "center",
                        minWidth: 0,
                      }}
                    >
                      <Avatar
                        size={32}
                        style={{
                          flex: "0 0 auto",
                          background: selected
                            ? OLIVEEB_COLORS.oliveebGreen
                            : OLIVEEB_COLORS.oliveebBlue,
                          color: OLIVEEB_COLORS.white,
                        }}
                      >
                        {(project.code || project.name || "P")
                          .slice(0, 2)
                          .toUpperCase()}
                      </Avatar>
                      <div style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: 800,
                            color: selected
                              ? OLIVEEB_COLORS.oliveebDarkGreen
                              : OLIVEEB_COLORS.oliveebDarkBlue,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {project.name}
                        </div>
                        <div
                          style={{
                            color: OLIVEEB_COLORS.textMuted,
                            fontSize: 12,
                            marginTop: 2,
                          }}
                        >
                          {project.code || "No code"} · {project.taskCount || 0}{" "}
                          tasks
                        </div>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {detailsProject && (
            <div
              style={{
                border: `1px solid ${OLIVEEB_COLORS.oliveebBlueSoft}`,
                borderRadius: 8,
                background: `linear-gradient(145deg, ${OLIVEEB_COLORS.white} 0%, ${OLIVEEB_COLORS.bgSoft} 100%)`,
                boxShadow: "0 16px 42px rgba(9, 3, 89, 0.09)",
                padding: 20,
                minWidth: 0,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  gap: 14,
                  alignItems: "flex-start",
                  flexWrap: "wrap",
                  marginBottom: 18,
                }}
              >
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      color: OLIVEEB_COLORS.oliveebGreen,
                      fontSize: 12,
                      fontWeight: 900,
                      textTransform: "uppercase",
                    }}
                  >
                    Selected Project
                  </div>
                  <h2
                    style={{
                      margin: "4px 0 6px",
                      color: OLIVEEB_COLORS.oliveebDarkBlue,
                      fontSize: 26,
                      lineHeight: 1.15,
                      wordBreak: "break-word",
                    }}
                  >
                    {detailsProject.name}
                  </h2>
                  <div
                    style={{
                      color: OLIVEEB_COLORS.textMuted,
                      fontSize: 13,
                      wordBreak: "break-word",
                    }}
                  >
                    {detailsProject.code || "No project code"}
                  </div>
                </div>

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  <Button onClick={() => openDetailsDrawer(detailsProject)}>
                    Open Drawer
                  </Button>
                  <Button
                    type="primary"
                    className="new-task-btn"
                    onClick={() => openEditDrawer(detailsProject)}
                  >
                    Edit
                  </Button>
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))",
                  gap: 12,
                  marginBottom: 18,
                }}
              >
                <ProjectDetailMetric
                  icon={<UserOutlined />}
                  label="Owner"
                  value={detailsProject.ownerName || "Unknown"}
                />
                <ProjectDetailMetric
                  icon={<FolderOpenOutlined />}
                  label="Iterations"
                  value={detailsProject.iterationCount || 0}
                />
                <ProjectDetailMetric
                  icon={<CheckSquareOutlined />}
                  label="Tasks"
                  value={detailsProject.taskCount || 0}
                />
                <ProjectDetailMetric
                  icon={<CheckSquareOutlined />}
                  label="Target Points"
                  value={detailsProject.targetPoints || 0}
                />
                <ProjectDetailMetric
                  icon={<CalendarOutlined />}
                  label="Created"
                  value={
                    detailsProject.createdAt
                      ? dayjs(detailsProject.createdAt).format(
                          "MMM D, YYYY HH:mm",
                        )
                      : "—"
                  }
                />
                <ProjectDetailMetric
                  icon={<CalendarOutlined />}
                  label="Updated"
                  value={
                    detailsProject.updatedAt
                      ? dayjs(detailsProject.updatedAt).format(
                          "MMM D, YYYY HH:mm",
                        )
                      : "—"
                  }
                />
              </div>

              {detailsProject.tags?.length > 0 && (
                <div style={{ marginBottom: 18 }}>
                  <div
                    style={{
                      color: OLIVEEB_COLORS.oliveebGreen,
                      fontWeight: 800,
                      marginBottom: 8,
                    }}
                  >
                    Tags
                  </div>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {detailsProject.tags.map((tag) => (
                      <span
                        key={tag.id || tag.name}
                        style={{
                          border: `1px solid ${OLIVEEB_COLORS.oliveebBlueSoft}`,
                          borderRadius: 999,
                          color: OLIVEEB_COLORS.oliveebBlue,
                          background: OLIVEEB_COLORS.oliveebBlueSoft,
                          fontSize: 12,
                          fontWeight: 800,
                          padding: "4px 10px",
                        }}
                      >
                        {tag.name || tag}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              <div>
                <div
                  style={{
                    color: OLIVEEB_COLORS.oliveebGreen,
                    fontWeight: 800,
                    marginBottom: 8,
                  }}
                >
                  Description
                </div>
                <div
                  style={{
                    border: `1px solid ${OLIVEEB_COLORS.border}`,
                    borderRadius: 8,
                    background: OLIVEEB_COLORS.white,
                    color: OLIVEEB_COLORS.textPrimary,
                    padding: 14,
                    minHeight: 120,
                    wordBreak: "break-word",
                  }}
                  dangerouslySetInnerHTML={{
                    __html: DOMPurify.sanitize(
                      detailsProject.description ||
                        "<p>No description provided.</p>",
                    ),
                  }}
                />
              </div>
            </div>
          )}
        </div>
      )}

      <ProjectDetailsDrawer
        visible={isDetailsDrawerOpen}
        onClose={() => setIsDetailsDrawerOpen(false)}
        project={editingProject}
        onEditProject={() => {}}
        onTagsUpdate={handleProjectTagsUpdate}
      />

      {/* drawer */}
      <ProjectFormDrawer
        open={isDrawerOpen}
        onClose={() => {
          if (!drawerLoading) {
            setIsDrawerOpen(false);
            setEditingProject(null);
          }
        }}
        mode={editingProject ? "edit" : "create"}
        initialValues={
          editingProject
            ? {
                ...editingProject,
                ownerName: editingProject.ownerName,
              }
            : {}
        }
        onSubmit={handleSubmitProject}
        loading={drawerLoading}
        onTagsUpdate={handleProjectTagsUpdate}
      />

      <ConfirmDeleteModal
        show={deleteModalOpen}
        setShow={setDeleteModalOpen}
        loading={deleteLoading}
        itemName="project"
        message={
          projectToDelete
            ? `Are you sure you want to delete "${projectToDelete.name}" project? This action cannot be undone.`
            : undefined
        }
        onConfirm={handleDeleteProject}
      />
    </div>
  );
};

const ProjectDetailMetric = ({ icon, label, value }) => (
  <div
    style={{
      border: `1px solid ${OLIVEEB_COLORS.oliveebGreenSoft}`,
      borderRadius: 8,
      background: OLIVEEB_COLORS.white,
      padding: 12,
      minWidth: 0,
    }}
  >
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 7,
        color: OLIVEEB_COLORS.oliveebGreen,
        fontSize: 12,
        fontWeight: 800,
        marginBottom: 6,
      }}
    >
      {icon}
      {label}
    </div>
    <div
      style={{
        color: OLIVEEB_COLORS.oliveebDarkBlue,
        fontWeight: 800,
        fontSize: 14,
        wordBreak: "break-word",
      }}
    >
      {value}
    </div>
  </div>
);

export default Projects;
